import React, { useState } from "react";
import { CheckCircle } from "react-feather";
import { Sheet, Button, Field, Chip, ChipRow, useInputStyle, useMoney, formatDateLong, todayIso } from "../ui";
import { BILL_FREQUENCIES, repeatUnitWord, MONTH_NAMES, scheduleLabel, nextDueDate, currentPeriodDate, nextOccurrence, frequencyOf } from "../../utils/billFrequency";
import { INVESTMENT_TYPES } from "../../utils/investmentTypes";
import { EXPENSE_TYPES, BILL_TYPES, INCOME_TYPES } from "../../utils/expenseTypes";
import { sourceRequired, bankAccountsOf, paidFromFields, receivedIntoId } from "../../utils/moneySources";
import MoneySourceSelect from "../../components/MoneySourceSelect";
import { useTheme } from "../../theme/ThemeContext";

// Bills and one-off expenses each offer their own types (see utils/expenseTypes).
// Income that can come on a schedule; a gift, borrowed money or "other" is a one-off
// already in hand.
const SCHEDULED_INCOME = ["Salary", "Payment"];
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

// What each button in the quick-add menu creates.
//   expense: money already spent          -> saved as Paid
//   bill:    something that repeats       -> saved as Pending (with its next due date)
//            (one-time ones are saved as an expense, or an investment, instead)
//   income:  money coming in              -> a salary or payment is saved as Upcoming on
//            its schedule (Finances > Income), and if its date has already come we
//            ask straight after saving whether it has arrived; a gift or "other" is
//            saved as Received
//   pastExpense / pastBill: something already paid on a past date -> saved as Paid,
//            one-time (a past bill has no frequency, just the date it was paid)
// No type (or bill frequency) is picked for you: they start on "Select" and must be
// chosen, so entries don't all end up under one default.
const MODES = {
  expense: { title: "Add expense", label: "Payee", placeholder: "e.g. Groceries", dateLabel: "Date", types: EXPENSE_TYPES },
  bill: { title: "Add a Bill & Payment", label: "Payee", placeholder: "e.g. Electricity", dateLabel: "Due date", types: BILL_TYPES },
  income: { title: "Add money received", label: "From", placeholder: "e.g. Salary from Infosys", dateLabel: "Date", types: INCOME_TYPES },
  pastExpense: { title: "Add past expense", label: "Payee", placeholder: "e.g. Groceries", dateLabel: "Date spent", types: EXPENSE_TYPES },
  pastBill: { title: "Add past Bill & Payment", label: "Payee", placeholder: "e.g. Electricity", dateLabel: "Past date (paid on)", types: BILL_TYPES },
};

// Yesterday, as YYYY-MM-DD: where a past entry's date starts.
const yesterdayIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Shown after saving income whose date has already come: did the money arrive?
const ReceivedPrompt = ({ entry, onAnswer }) => {
  const { tokens } = useTheme();
  const money = useMoney();
  const [busy, setBusy] = useState(false);
  const repeats = entry.repeatUnit && entry.repeatUnit !== "once";
  const isToday = entry.date === todayIso();
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, color: tokens.positive }}>
        <CheckCircle size={22} />
        <span style={{ fontSize: 15, fontWeight: 700, color: tokens.textPrimary }}>Saved to Income</span>
      </div>
      <p style={{ margin: "0 0 6px", fontSize: 15, color: tokens.textPrimary }}>
        {entry.source} · <strong>{money(entry.amount)}</strong> was expected {isToday ? "today" : `on ${formatDateLong(entry.date)}`}.
      </p>
      <p style={{ margin: "0 0 18px", fontSize: 15, fontWeight: 700, color: tokens.textPrimary }}>Have you received it?</p>
      {repeats && (
        <p style={{ margin: "0 0 16px", fontSize: 12, color: tokens.textMuted }}>
          When you mark it received, the next one ({formatDateLong(nextOccurrence(entry.date, entry.repeatUnit, entry.repeatEvery))}) is lined up automatically.
        </p>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Button disabled={busy} onClick={() => { setBusy(true); onAnswer(true); }}>Yes, mark as received</Button>
        <Button kind="ghost" disabled={busy} onClick={() => onAnswer(false)}>Not yet</Button>
      </div>
    </div>
  );
};

// `mode` is what the caller asked for. Every mode maps to exactly one kind of
// entry, except "activity" (opened from the Activity tab's + button), which
// offers an expense, money received, or a past entry (expense or bill).
//
// Given a `bill` (with mode "bill"), the same form edits that bill instead: filled
// in with its details, and saving updates it. `onCancel` goes back without saving.
const AddEntrySheet = ({ mode, data, onClose, bill = null, onCancel }) => {
  const input = useInputStyle();
  const editing = !!bill;
  const togglable = mode === "activity";
  const [kind, setKind] = useState(togglable ? "expense" : mode);
  const config = MODES[kind];
  // An entry made from a loan or investment keeps that one's frequency.
  const locked = editing && !!bill.sourceKind;
  const [form, setForm] = useState(() => {
    if (!editing) {
      const now = new Date();
      return {
        label: "",
        type: "",
        investmentType: INVESTMENT_TYPES[0],
        returnRate: "",
        date: todayIso(),
        dueDay: String(now.getDate()),
        dueMonth: String(now.getMonth()),
        amount: "",
        description: "",
        autopay: false,
        repeatUnit: "",
        repeatEvery: "1",
        billerId: "", // past Bill & Payment: "" not chosen, "new", or an existing biller's id
        paidFrom: "", // "" not tracked, "account:<id>" or "card:<id>"
        variableAmount: false, // a bill whose amount changes each time: the amount is an estimate
      };
    }
    const { repeatUnit, repeatEvery } = frequencyOf(bill);
    const due = new Date(`${bill.dueDate}T00:00:00`);
    // The investment this bill is linked to, if any, so its kind can be shown and changed.
    const asset = bill.sourceKind === "asset" && data.assets ? data.assets.find((a) => a.id === bill.sourceId) : null;
    return {
      label: bill.payee,
      type: bill.type,
      investmentType: asset && INVESTMENT_TYPES.includes(asset.type) ? asset.type : INVESTMENT_TYPES[0],
      returnRate: asset && asset.returnRate != null ? String(asset.returnRate) : "",
      date: bill.dueDate,
      dueDay: String(due.getDate()),
      dueMonth: String(due.getMonth()),
      amount: String(bill.amount),
      description: bill.description || "",
      autopay: !!bill.autopay,
      repeatUnit,
      repeatEvery: String(repeatEvery),
      paidFrom: bill.paidFromKind ? `${bill.paidFromKind}:${bill.paidFromId}` : "",
      variableAmount: !!bill.variableAmount,
    };
  });
  // An older bill may have a type that's no longer offered; keep it selectable.
  const types = editing && bill.type && !config.types.includes(bill.type) ? [bill.type, ...config.types] : config.types;
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [askReceived, setAskReceived] = useState(null); // the saved income entry, when its date has come
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const isBill = kind === "bill";
  const isIncome = kind === "income";
  const isPast = kind === "pastExpense" || kind === "pastBill";
  const isInvestment = !isIncome && form.type === "Investments";
  // Bills and salary/payment income have a frequency; everything else is one date.
  const scheduled = isBill || (isIncome && SCHEDULED_INCOME.includes(form.type));
  const repeats = scheduled && !!form.repeatUnit && form.repeatUnit !== "once";

  // A past Bill & Payment is a payment of a bill already on Bills & Payments (its name
  // and type come from there), or of a new one, which is added there too.
  const isPastBill = kind === "pastBill";
  const billers = data.billers || [];
  const pickedBiller = isPastBill && form.billerId && form.billerId !== "new" ? billers.find((b) => String(b.id) === form.billerId) : null;
  const newPastBiller = isPastBill && !pickedBiller && (form.billerId === "new" || billers.length === 0);
  const pastRepeats = newPastBiller && !!form.repeatUnit && form.repeatUnit !== "once";

  // Where the money goes out of: a bank account (its value goes down) or a credit card
  // (what's owed on it goes up), once the entry is paid. For money received, the bank
  // account it goes into. Required for anything dated from the day the first account
  // or card was added.
  const bankAccounts = bankAccountsOf(data.assets);
  const cards = data.cards || [];
  const paidFromBody = () => paidFromFields(form.paidFrom);
  const sourceNeeded = (date) => sourceRequired(date, data.assets, cards) && (isIncome ? bankAccounts.length > 0 : true);

  const pickBiller = (e) => {
    const id = e.target.value;
    const biller = billers.find((b) => String(b.id) === id);
    // Start from its usual amount; it can be changed for this payment.
    setForm((f) => ({ ...f, billerId: id, amount: biller && !f.amount ? String(biller.amount) : f.amount }));
  };

  const switchKind = (next) => {
    setKind(next);
    const wasPast = kind === "pastExpense" || kind === "pastBill";
    const toPast = next === "pastExpense" || next === "pastBill";
    // A past entry starts on yesterday; switching back out of one starts on today.
    // A card can't receive money, so the account / card choice starts over too.
    setForm((f) => ({ ...f, type: "", repeatUnit: "", billerId: "", paidFrom: "", date: toPast ? (wasPast ? f.date : yesterdayIso()) : wasPast ? todayIso() : f.date }));
  };

  // Monthly: pick the day. Yearly: the month and day. Otherwise a full date.
  // A bill is due the next time that day comes; income is this month's (or year's)
  // one, which may already have arrived - we ask.
  // When editing, the day moves within the bill's own month (or year), so an overdue
  // one isn't pushed on to next month.
  const dateOf = () => {
    if (!repeats || form.repeatUnit === "days") return form.date;
    const pick = { day: parseInt(form.dueDay, 10), month: parseInt(form.dueMonth, 10) };
    if (editing) return currentPeriodDate(form.repeatUnit, pick, new Date(`${bill.dueDate}T00:00:00`));
    return isIncome ? currentPeriodDate(form.repeatUnit, pick) : nextDueDate(form.repeatUnit, pick);
  };

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    const repeatEvery = parseInt(form.repeatEvery, 10);
    if (isPastBill && !pickedBiller && !newPastBiller) return setError("Choose which bill it was, or add a new one.");
    if (!pickedBiller && !form.label.trim()) return setError(isIncome ? "Who is it from?" : "Payee is required.");
    if (Number.isNaN(amount) || amount < 0) return setError("Enter a valid amount.");
    if (!pickedBiller && !form.type) return setError("Choose a type.");
    const returnRate = form.returnRate.trim() === "" ? null : parseFloat(form.returnRate);
    if (isInvestment && Number.isNaN(returnRate)) return setError("Enter the return as a number, or leave it empty.");
    if (isPast && (!form.date || form.date > todayIso())) return setError("Choose a past date (today or earlier).");
    if ((scheduled || newPastBiller) && !form.repeatUnit) return setError("Choose how often it comes.");
    if ((repeats || pastRepeats) && (Number.isNaN(repeatEvery) || repeatEvery < 1 || repeatEvery > 365)) return setError("Enter how often it repeats, from 1 to 365.");
    const date = dateOf();
    if (!form.paidFrom && sourceNeeded(date)) return setError(isIncome ? "Choose the account it goes into." : "Choose where it's paid from.");
    setSaving(true);
    setError("");
    const repeatUnit = repeats || pastRepeats ? form.repeatUnit : "once";
    const every = repeats || pastRepeats ? repeatEvery : 1;
    const into = { receivedIntoId: receivedIntoId(form.paidFrom) };
    try {
      if (pickedBiller) {
        // The server settles that bill's unpaid entry for the month, if it has one.
        await data.create("bills", {
          billerId: pickedBiller.id,
          payee: pickedBiller.name,
          type: pickedBiller.type,
          schedule: "One-time",
          dueDate: form.date,
          amount,
          status: "Paid",
          description: form.description,
          repeatUnit: "once",
          repeatEvery: 1,
          ...paidFromBody(),
        });
        return onClose();
      }
      if (isIncome) {
        if (!scheduled) {
          // A gift, borrowed money or "other": already in hand (or, dated ahead, still to come).
          await data.create("income", { source: form.label, type: form.type, date, amount, status: date > todayIso() ? "Upcoming" : "Received", description: form.description, ...into });
          return onClose();
        }
        const row = await data.create("income", { source: form.label, type: form.type, date, amount, status: "Upcoming", description: form.description, repeatUnit, repeatEvery: every, ...into });
        if (date <= todayIso()) {
          setSaving(false);
          return setAskReceived(row);
        }
        return onClose();
      }
      const body = {
        payee: form.label.trim(),
        type: form.type,
        schedule: scheduleLabel(repeatUnit, every),
        dueDate: date,
        amount,
        autopay: isBill && repeats && form.autopay,
        description: form.description,
        repeatUnit,
        repeatEvery: every,
        // Saved on the investment this is linked to, on the Finances tab.
        ...(isInvestment ? { investmentType: form.investmentType, returnRate } : {}),
        ...paidFromBody(),
        // Only a repeating bill can have an amount that changes each time.
        ...(isBill ? { variableAmount: repeats && form.variableAmount } : {}),
      };
      // Editing keeps the bill's paid / unpaid status as it is.
      if (editing) await data.update("bills", bill.id, body);
      // A one-time entry from the bill form is an expense: Paid if its date has
      // come, Pending (and reminded) if it's still ahead.
      else await data.create("bills", { ...body, status: !isBill ? "Paid" : repeats || date > todayIso() ? "Pending" : "Paid" });
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  // Marking received counts it in "Money in" and, for a repeating one, has the
  // server line up the next; reload to show that.
  const answerReceived = async (received) => {
    if (received) {
      await data.update("income", askReceived.id, { status: "Received" });
      data.reload();
    }
    onClose();
  };

  if (askReceived) {
    return (
      <Sheet title="Money received?" onClose={onClose}>
        <ReceivedPrompt entry={askReceived} onAnswer={answerReceived} />
      </Sheet>
    );
  }

  const typeField = (
    <>
      <Field label="Type">
        <select style={input} value={form.type} onChange={set("type")}>
          <option value="" disabled>Select</option>
          {types.map((t) => <option key={t}>{t}</option>)}
        </select>
      </Field>
      {isInvestment && (
        <Field label="Investment type">
          <select style={input} value={form.investmentType} onChange={set("investmentType")}>
            {INVESTMENT_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
      )}
      {isInvestment && (
        <Field label="Return % per year (optional)">
          <input style={input} type="number" inputMode="decimal" step="0.01" value={form.returnRate} onChange={set("returnRate")} placeholder="e.g. 12" />
        </Field>
      )}
    </>
  );

  const dayField = (
    <Field label={form.repeatUnit === "years" ? "Day" : isIncome ? "Comes on day" : "Due on day"}>
      <select style={input} value={form.dueDay} onChange={set("dueDay")}>
        {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
    </Field>
  );

  const scheduleFields = (
    <>
      <Field label="Frequency">
        <select style={input} value={form.repeatUnit} onChange={set("repeatUnit")} disabled={locked}>
          <option value="" disabled>Select</option>
          {BILL_FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </Field>
      {repeats && (
        <Field label="Repeats every">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input style={{ ...input, width: 90 }} type="number" min="1" max="365" inputMode="numeric" value={form.repeatEvery} onChange={set("repeatEvery")} disabled={locked} />
            <span style={{ fontSize: 14 }}>{repeatUnitWord(form.repeatUnit, form.repeatEvery)}</span>
          </div>
        </Field>
      )}
      {form.repeatUnit === "months" && dayField}
      {form.repeatUnit === "years" && (
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 2, minWidth: 0 }}>
            <Field label={isIncome ? "Comes in" : "Due in"}>
              <select style={input} value={form.dueMonth} onChange={set("dueMonth")}>
                {MONTH_NAMES.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>{dayField}</div>
        </div>
      )}
      {(form.repeatUnit === "once" || form.repeatUnit === "days") && (
        <Field label={form.repeatUnit === "days" ? (isIncome ? "First one on" : "First due on") : "Date"}>
          <input style={input} type="date" value={form.date} onChange={set("date")} />
        </Field>
      )}
    </>
  );

  return (
    <Sheet title={editing ? `Edit ${bill.payee}` : config.title} onClose={onClose}>
      <form onSubmit={submit}>
        {togglable && (
          <ChipRow>
            <Chip active={kind === "expense"} onClick={() => switchKind("expense")}>Expense</Chip>
            <Chip active={isIncome} onClick={() => switchKind("income")}>Money received</Chip>
            <Chip active={isPast} onClick={() => !isPast && switchKind("pastExpense")}>Past</Chip>
          </ChipRow>
        )}
        {togglable && isPast && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 5, fontSize: 12, fontWeight: 600, opacity: 0.75 }}>What was it?</div>
            <ChipRow>
              <Chip active={kind === "pastExpense"} onClick={() => switchKind("pastExpense")}>Expense</Chip>
              <Chip active={kind === "pastBill"} onClick={() => switchKind("pastBill")}>Bill & Payment</Chip>
            </ChipRow>
          </div>
        )}
        {isPastBill && billers.length > 0 && (
          <Field label="Which bill?">
            <select style={input} value={form.billerId} onChange={pickBiller}>
              <option value="" disabled>Select</option>
              {billers.map((b) => (
                <option key={b.id} value={b.id}>{b.name} · {b.type}</option>
              ))}
              <option value="new">+ A new bill or payment</option>
            </select>
          </Field>
        )}
        {!pickedBiller && (
          <Field label={config.label}>
            <input style={input} autoFocus={!isPastBill} value={form.label} onChange={set("label")} placeholder={config.placeholder} />
          </Field>
        )}
        <Field label={isBill && form.variableAmount ? "Usual amount (₹) - an estimate" : "Amount (₹)"}>
          <input style={input} type="number" inputMode="decimal" value={form.amount} onChange={set("amount")} placeholder="0" />
        </Field>

        {isBill && (
          <>
            {scheduleFields}
            {locked && (
              <p style={{ margin: "-4px 0 12px", fontSize: 12, opacity: 0.75 }}>
                This entry repeats on the schedule of your {bill.sourceKind === "loan" ? "loan" : "investment"}. Change the frequency there, on the Finances tab.
              </p>
            )}
            {!locked && form.repeatUnit === "once" && (
              <p style={{ margin: "-4px 0 12px", fontSize: 12, opacity: 0.75 }}>
                {form.type === "Investments"
                  ? "One-time investments are added to your investments on the Finances tab, not to Bills & Payments."
                  : editing
                    ? "As a one-time payment it moves out of Bills & Payments, into Activity as an expense."
                    : "One-time payments are saved as an expense (in Activity), not in Bills & Payments."}
              </p>
            )}
            {typeField}
          </>
        )}
        {isIncome && (
          <>
            {/* The type decides whether there's a schedule, so it comes first here. */}
            {typeField}
            {scheduled ? (
              <>
                {scheduleFields}
                {form.repeatUnit && (
                  <p style={{ margin: "-4px 0 12px", fontSize: 12, opacity: 0.75 }}>
                    Saved under Income on the Finances tab{repeats ? ", and the next one is lined up each time you receive it" : ""}.
                  </p>
                )}
              </>
            ) : (
              <Field label={config.dateLabel}>
                <input style={input} type="date" value={form.date} onChange={set("date")} />
              </Field>
            )}
          </>
        )}
        {(kind === "expense" || isPast) && (
          <>
            <Field label={config.dateLabel}>
              <input style={input} type="date" value={form.date} onChange={set("date")} max={isPast ? todayIso() : undefined} />
            </Field>
            {!pickedBiller && typeField}
            {newPastBiller && (
              <>
                <Field label="How often does it come?">
                  <select style={input} value={form.repeatUnit} onChange={set("repeatUnit")}>
                    <option value="" disabled>Select</option>
                    {BILL_FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </Field>
                {pastRepeats && (
                  <Field label="Repeats every">
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <input style={{ ...input, width: 90 }} type="number" min="1" max="365" inputMode="numeric" value={form.repeatEvery} onChange={set("repeatEvery")} />
                      <span style={{ fontSize: 14 }}>{repeatUnitWord(form.repeatUnit, form.repeatEvery)}</span>
                    </div>
                  </Field>
                )}
              </>
            )}
            {isPast && (
              <p style={{ margin: "-4px 0 12px", fontSize: 12, opacity: 0.75 }}>
                {pickedBiller
                  ? `Saved as paid on that date, in Activity and in the history of ${pickedBiller.name}. If ${pickedBiller.name} had an unpaid entry that month, it's marked paid.`
                  : pastRepeats
                    ? "Saved as paid on that date, and added to Bills & Payments with the next one lined up after it."
                    : newPastBiller && form.repeatUnit === "once"
                      ? "Saved as a one-time payment on that date, in Activity. Only repeating ones go on Bills & Payments."
                      : "Saved as already paid on that date."}
              </p>
            )}
          </>
        )}

        {(bankAccounts.length > 0 || (!isIncome && cards.length > 0)) ? (
          <>
            <Field label={isIncome ? "Received into" : "Paid from"}>
              <MoneySourceSelect
                style={input}
                value={form.paidFrom}
                onChange={(v) => setForm((f) => ({ ...f, paidFrom: v }))}
                assets={data.assets}
                cards={cards}
                accountsOnly={isIncome}
                required={sourceNeeded(dateOf())}
              />
            </Field>
            {form.paidFrom && (
              <p style={{ margin: "-6px 0 12px", fontSize: 12, opacity: 0.75 }}>
                {isIncome
                  ? `Added to this account's balance${scheduled ? " each time you mark it received" : ""}.`
                  : `${form.paidFrom.startsWith("card:") ? "Added to what you owe on this card" : "Taken off this account's balance"}${isBill && !editing ? " each time you mark it paid." : isBill ? " when it's marked paid." : "."}`}
              </p>
            )}
          </>
        ) : (
          <p style={{ margin: "-4px 0 12px", fontSize: 12, opacity: 0.75 }}>
            Add a bank account{isIncome ? "" : " or credit card"} on Finances to pick where the money {isIncome ? "goes" : "comes from"}.
          </p>
        )}
        <Field label="Description (optional)">
          <input style={input} value={form.description} onChange={set("description")} placeholder="A note for later" />
        </Field>
        {isBill && repeats && (
          <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, fontSize: 14 }}>
            <input type="checkbox" checked={form.autopay} onChange={(e) => setForm((f) => ({ ...f, autopay: e.target.checked }))} style={{ width: 20, height: 20 }} />
            Paid automatically (autopay)
          </label>
        )}
        {isBill && repeats && (
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14, fontSize: 14 }}>
            <input type="checkbox" checked={form.variableAmount} onChange={(e) => setForm((f) => ({ ...f, variableAmount: e.target.checked }))} style={{ width: 20, height: 20, marginTop: 1 }} />
            <span>
              Amount changes each time
              <span style={{ display: "block", fontSize: 12, opacity: 0.75 }}>
                Like electricity, water or a card bill. The amount above is an estimate; you enter the actual one when you mark it paid, and the next one is estimated from your last few payments.
              </span>
            </span>
          </label>
        )}
        {error && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: "crimson" }}>{error}</div>}
        <Button type="submit" disabled={saving} style={{ width: "100%" }}>{saving ? "Saving…" : editing ? "Save changes" : "Save"}</Button>
        {editing && onCancel && (
          <Button type="button" kind="ghost" disabled={saving} onClick={onCancel} style={{ width: "100%", marginTop: 10 }}>Cancel</Button>
        )}
      </form>
    </Sheet>
  );
};

export default AddEntrySheet;
